import {useCallback, useEffect, useState} from 'react'
import type {RefObject} from 'react'
import type Hls from 'hls.js'

const NATIVE_HLS_MIME = 'application/vnd.apple.mpegurl'

interface HlsVideoSource {
  streamingUrl: string
  mp4Url: string | null
}

interface HlsVideoState {
  hasError: boolean
  retry: () => void
}

/**
 * Attaches a DatoCMS (Mux) HLS stream to a `<video>` element. `hls.js` (imported on demand so it stays out of the main
 * bundle) is preferred wherever Media Source Extensions exist because it is the most dependable path; browsers
 * without it but with native HLS (iPhone Safari) play the stream directly. The progressive mp4 rendition is the last
 * resort, also used when the stream fails fatally.
 */
export const useHlsVideo = (
  videoRef: RefObject<HTMLVideoElement | null>,
  {streamingUrl, mp4Url}: HlsVideoSource,
): HlsVideoState => {
  const [hasError, setHasError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let isCancelled = false
    let isUsingMp4 = false
    let hls: Hls | null = null
    setHasError(false)

    const canPlayNativeHls = (): boolean => video.canPlayType(NATIVE_HLS_MIME) !== ''

    const fallBackToMp4 = (): void => {
      if (isCancelled) return
      if (mp4Url === null || isUsingMp4) {
        setHasError(true)
        return
      }
      isUsingMp4 = true
      hls?.destroy()
      hls = null
      video.src = mp4Url
    }

    const playNatively = (): void => {
      if (canPlayNativeHls()) {
        video.src = streamingUrl
      } else {
        fallBackToMp4()
      }
    }

    const handleVideoError = (): void => fallBackToMp4()
    video.addEventListener('error', handleVideoError)

    import('hls.js')
      .then(({default: HlsClass}) => {
        if (isCancelled) return
        if (!HlsClass.isSupported()) {
          playNatively()
          return
        }

        hls = new HlsClass()
        hls.on(HlsClass.Events.ERROR, (_event, data) => {
          if (data.fatal) fallBackToMp4()
        })
        hls.loadSource(streamingUrl)
        hls.attachMedia(video)
      })
      .catch(playNatively)

    return () => {
      isCancelled = true
      video.removeEventListener('error', handleVideoError)
      hls?.destroy()
      video.removeAttribute('src')
      video.load()
    }
  }, [videoRef, streamingUrl, mp4Url, attempt])

  const retry = useCallback((): void => setAttempt(value => value + 1), [])

  return {hasError, retry}
}
